// src/models/HoursAccountModel.js

// ---------------------------------------------------------------------------
// HoursAdjustmentType
// ---------------------------------------------------------------------------

export const HoursAdjustmentType = Object.freeze({
  ADMIN_ADJUSTMENT: 'admin_adjustment',
  TIME_CORRECTION:  'time_correction',
  MIGRATION:        'migration',
});

// ---------------------------------------------------------------------------
// HoursAdjustmentTypeLabels
// ---------------------------------------------------------------------------

export const HoursAdjustmentTypeLabels = Object.freeze({
  admin_adjustment: 'Admin Adjustment',
  time_correction:  'Time Correction',
  migration:        'Migration Balance',
});

// ---------------------------------------------------------------------------
// HoursAdjustment
// ---------------------------------------------------------------------------

export class HoursAdjustment {
  constructor({
    type      = HoursAdjustmentType.ADMIN_ADJUSTMENT,
    hours     = 0,
    note      = '',
    createdAt = new Date(),
    createdBy = '',
  } = {}) {
    this.type      = type;
    this.hours     = hours;
    this.note      = note;
    this.createdAt = createdAt instanceof Date
      ? createdAt
      : new Date(createdAt);
    this.createdBy = createdBy;
  }

  toFirestore() {
    return {
      type:      this.type,
      hours:     this.hours,
      note:      this.note,
      createdAt: this.createdAt,
      createdBy: this.createdBy,
    };
  }

  static fromFirestore(data) {
    return new HoursAdjustment({
      ...data,
      createdAt: data.createdAt?.toDate
        ? data.createdAt.toDate()
        : new Date(data.createdAt),
    });
  }
}

// ---------------------------------------------------------------------------
// MonthlyBalance  (read-only — Cloud Function writes, web only reads)
// ---------------------------------------------------------------------------

export class MonthlyBalance {
  static fromFirestore(data) {
    return {
      worked:       data.worked,
      target:       data.target,
      balance:      data.balance,
      source:       data.source,
      closed:       data.closed,
      calculatedAt: data.calculatedAt?.toDate
        ? data.calculatedAt.toDate()
        : new Date(data.calculatedAt),
    };
  }
}

// ---------------------------------------------------------------------------
// HoursAccount  (default export)
// ---------------------------------------------------------------------------

class HoursAccount {
  constructor({
    year              = new Date().getFullYear().toString(),
    openingBalance    = 0,
    adjustments       = [],
    monthlyBalances   = {},
    ytdBalance        = 0,
    closingBalance    = null,
    carryOverUncapped = true,
    createdAt         = new Date(),
    updatedAt         = new Date(),
    lastCalculatedAt  = null,
  } = {}) {
    this.year             = year;
    this.openingBalance   = openingBalance;
    this.ytdBalance       = ytdBalance;
    this.closingBalance   = closingBalance;
    this.carryOverUncapped = carryOverUncapped;

    this.createdAt = createdAt instanceof Date
      ? createdAt
      : new Date(createdAt);

    this.updatedAt = updatedAt instanceof Date
      ? updatedAt
      : new Date(updatedAt);

    this.lastCalculatedAt = lastCalculatedAt
      ? (lastCalculatedAt instanceof Date
          ? lastCalculatedAt
          : new Date(lastCalculatedAt))
      : null;

    // Coerce adjustments — each element must be an HoursAdjustment instance
    this.adjustments = adjustments.map(a =>
      a instanceof HoursAdjustment ? a : new HoursAdjustment(a)
    );

    // Coerce monthlyBalances — each value must be a MonthlyBalance plain object
    this.monthlyBalances = Object.fromEntries(
      Object.entries(monthlyBalances).map(([k, v]) => [
        k,
        MonthlyBalance.fromFirestore(v),
      ])
    );
  }

  // -------------------------------------------------------------------------
  // Computed helpers
  // -------------------------------------------------------------------------

  getTotalAdjustmentHours() {
    return this.adjustments.reduce((sum, adj) => sum + adj.hours, 0);
  }

  getClosedMonthsBalance() {
    return Object.values(this.monthlyBalances)
      .filter(m => m.closed)
      .reduce((sum, m) => sum + m.balance, 0);
  }

  // -------------------------------------------------------------------------
  // Recalculate ytdBalance  (call after any web-layer write)
  // -------------------------------------------------------------------------

  recalculate() {
    this.ytdBalance =
      this.openingBalance +
      this.getTotalAdjustmentHours() +
      this.getClosedMonthsBalance();
    this.updatedAt = new Date();
  }

  // -------------------------------------------------------------------------
  // Firestore serialisation
  // -------------------------------------------------------------------------

  toFirestore() {
    return {
      year:             this.year,
      openingBalance:   this.openingBalance,
      adjustments:      this.adjustments.map(a => a.toFirestore()),
      monthlyBalances:  Object.fromEntries(
        Object.entries(this.monthlyBalances).map(([k, v]) => [
          k,
          { ...v, calculatedAt: v.calculatedAt },
        ])
      ),
      ytdBalance:        this.ytdBalance,
      closingBalance:    this.closingBalance,
      carryOverUncapped: this.carryOverUncapped,
      createdAt:         this.createdAt,
      updatedAt:         this.updatedAt,
      lastCalculatedAt:  this.lastCalculatedAt,
    };
  }

  // -------------------------------------------------------------------------
  // Firestore deserialisation
  // -------------------------------------------------------------------------

  static fromFirestore(data) {
    return new HoursAccount({
      ...data,
      adjustments: (data.adjustments || []).map(a =>
        HoursAdjustment.fromFirestore(a)
      ),
      monthlyBalances: Object.fromEntries(
        Object.entries(data.monthlyBalances || {}).map(([k, v]) => [
          k,
          MonthlyBalance.fromFirestore(v),
        ])
      ),
      createdAt: data.createdAt?.toDate
        ? data.createdAt.toDate()
        : new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate
        ? data.updatedAt.toDate()
        : new Date(data.updatedAt),
      lastCalculatedAt: data.lastCalculatedAt?.toDate
        ? data.lastCalculatedAt.toDate()
        : data.lastCalculatedAt
          ? new Date(data.lastCalculatedAt)
          : null,
    });
  }

  // -------------------------------------------------------------------------
  // Factory
  // -------------------------------------------------------------------------

  static createDefault(year, openingBalance = 0) {
    const account = new HoursAccount({
      year:             year.toString(),
      openingBalance,
      adjustments:      [],
      monthlyBalances:  {},
      carryOverUncapped: true,
    });
    account.recalculate();
    return account;
  }
}

export default HoursAccount;